import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaGithub, FaLinkedin, FaTwitter, FaMedium} from 'react-icons/fa';
import profilePicture from '../assets/images/Nahom Mengistu Birhan.png';
import '../assets/components/_home.scss';

// Import DCInject preview
import dcinjectBlockDiagram from '../assets/images/DCIject/DCInject-block-diagram.png';


const Home = () => {
  return (
    <>
      <Helmet>
        <title>Nahom Mengistu Birhan - AI Security Researcher & Machine Learning Engineer | Old Dominion University</title>
        <meta name="description" content="Nahom Mengistu Birhan - AI Security Researcher specializing in LLM jailbreak detection and defense. Machine Learning Engineer with 5+ years experience in cybersecurity, data analytics, and edge computing at Old Dominion University." />
        <meta name="keywords" content="Nahom, Nahom Mengistu, Nahom Birhan, Nahom M. Birhan, Nahom Mengistu Birhan, ናሆም, ናሆም መንግስቱ, ናሆም ብርሃን, ናሆም መንግስቱ ብርሃን, AI Security, Machine Learning, LLM Security, Jailbreak Detection, Cybersecurity, Data Analytics, Edge Computing, TensorFlow, PyTorch, ReactJS, Python, Research Assistant, Old Dominion University, Norfolk VA" />

        {/* Open Graph */}
        <meta property="og:title" content="Nahom Mengistu Birhan - AI Security Researcher & Machine Learning Engineer" />
        <meta property="og:description" content="Nahom Mengistu Birhan - AI Security Researcher specializing in LLM jailbreak detection and defense. Machine Learning Engineer with 5+ years experience in cybersecurity, data analytics, and edge computing." />
        <meta property="og:type" content="profile" />
        <meta property="og:image" content={profilePicture} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nahom Mengistu Birhan - AI Security Researcher & Machine Learning Engineer" />
        <meta name="twitter:description" content="Nahom Mengistu Birhan - AI Security Researcher specializing in LLM jailbreak detection and defense. Machine Learning Engineer with 5+ years experience in cybersecurity, data analytics, and edge computing." />
        <meta name="twitter:image" content={profilePicture} />

        {/* Additional SEO */}
        <meta name="author" content="Nahom Mengistu Birhan" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://nahomcyberai.tech/" />

        {/* Structured Data */}
        <script type="application/ld+json">
        {`{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Nahom Mengistu Birhan",
          "alternateName": [
            "Nahom M. Birhan",
            "Nahom",
            "Nahom Mengistu",
            "Nahom M Birhan",
            "Nahom Mengistu Birhan",
            "Nahom Birhan",
            "ናሆም መንግስቱ ብርሃን",
            "ናሆም መንግስቱ",
            "ናሆም ብርሃን",
            "ናሆም"
          ],
          "jobTitle": "AI Security Researcher & Machine Learning Engineer",
          "description": "AI Security Researcher specializing in LLM jailbreak detection and defense. Machine Learning Engineer with 5+ years experience in cybersecurity, data analytics, and edge computing.",
          "url": "https://nahomcyberai.tech",
          "image": "https://nahomcyberai.tech/Nahom%20Mengistu%20Birhan.png",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Norfolk",
            "addressRegion": "VA",
            "addressCountry": "US"
          },
          "worksFor": {
            "@type": "Organization",
            "name": "Old Dominion University",
            "url": "https://www.odu.edu"
          },
          "knowsAbout": [
            "AI Security",
            "Robustness",
            "Human Alignment",
            "Privacy",
            "Privacy-Preserving Machine Learning",
            "Federated Learning",
            "Personalized Federated Learning",
            "Agentic AI",
            "Agentic AI Security",
            "LLM Security",
            "Jailbreak Detection",
            "Backdoor Detection",
            "Multimodal Language Models",
            "Natural Language Processing",
            "Reinforcement Learning",
            "Reinforcement Learning Preference Optimization",
            "Machine Learning",
            "Deep Learning",
            "Cybersecurity",
            "Data Analytics",
            "Edge Computing",
            "TensorFlow",
            "PyTorch",
            "Python",
            "ReactJS"
          ],
          "sameAs": [
            "https://www.linkedin.com/in/nahombirhan",
            "https://github.com/NahomMA",
            "https://x.com/NahomMengistu10",
            "https://medium.com/@nahombirhan"
          ]
        }`}
        </script>
      </Helmet>

      <main className="home-container" role="main">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <div className="hero-image">
              <img
                src={profilePicture}
                alt="Nahom Mengistu Birhan - AI Security Researcher"
                className="profile-image"
                loading="eager"
              />
            </div>
            <div className="hero-text">
              <h1 className="hero-title">Nahom Mengistu Birhan</h1>
              <h2 className="hero-subtitle">AI Security Researcher & Machine Learning Engineer</h2>
              <p className="hero-description">
                Research Assistant at Old Dominion University specializing in AI security, robustness, human alignment, and privacy.
              </p>

            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="about-section">
          <div className="section-header">
            <h2>About Me</h2>
            <p>AI Security, Robustness, Human Alignment, Privacy</p>
          </div>
          <div className="about-content">
            <div className="about-text">
              <p>
                I am a Ph.D. student and Research Assistant at Old Dominion University working on the security
                of AI systems — from jailbreak and backdoor attacks in large language and multimodal models to
                the defense of autonomous, tool-using agents. With 3+ years of industry experience, I've built
                applied ML proof-of-concept projects and enterprise software solutions.
              </p>
              <p>
                My research focuses on AI robustness, alignment, and security-first model design: measuring how
                models fail under adversarial pressure, and building defenses with guarantees rather than hope.
              </p>
            </div>
            <div className="about-highlights">
              <div className="highlight-item">
                <span className="highlight-number">10+</span>
                <span className="highlight-label">Research & Engineering Projects</span>
              </div>
              <div className="highlight-item">
                <span className="highlight-number">3+</span>
                <span className="highlight-label">Years Industry Experience</span>
              </div>
            </div>

          </div>
        </section>

        <section className="featured-section">
          <div className="section-header">
            <h2>Research & Projects</h2>
            <p>Publications, research under review, ongoing work, course projects, industry work, and experiments</p>
          </div>

          <div className="research-categories">
            {/* Publications */}
            <div className="category-section">
              <h3 className="category-title">
                <span className="category-icon"></span>
                Publications
              </h3>
              <div className="featured-grid">
                <div className="featured-card research-card full-width-card">
                  <div className="card-header">
                    <span className="card-badge ongoing">Published: Oral Presentation</span>
                  </div>
                  <h4>DCInject: Persistent Backdoor Attacks via Frequency Manipulation in Personalized Federated Learning</h4>
                  <p>Adaptive frequency-domain backdoor attack for PFL that achieves superior attack success rates while maintaining clean accuracy.</p>

                  <div className="card-preview-images" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '1.5rem',
                    margin: '1.5rem 0'
                  }}>
                    <div className="preview-item" style={{ textAlign: 'center' }}>
                      <img src={dcinjectBlockDiagram} alt="DCInject Block Diagram" style={{ width: '100%', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} />
                      <p style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem', fontWeight: '500' }}>Method: Frequency Manipulation</p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
                    <span className="coming-soon">ICASSP 2026</span>
                    <Link to="/projects" style={{ fontSize: '0.9rem', color: '#3182ce', fontWeight: '600', textDecoration: 'none' }}>
                      View Technical Details <FaArrowRight style={{ fontSize: '0.8rem' }} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Under Review & Ongoing — described generally while peer review is in progress */}
            <div className="category-section">
              <h3 className="category-title">
                <span className="category-icon"></span>
                Under Review & Ongoing Research
              </h3>
              <div className="featured-grid">
                <div className="featured-card research-card">
                  <div className="card-header">
                    <span className="card-badge ongoing">Under Review · 2026</span>
                  </div>
                  <h4>Measuring LLM Susceptibility to Tool-Use Manipulation</h4>
                  <p>
                    A controlled evaluation of how adversarial content reaching a language model through its
                    tooling can redirect the model's behavior — designed to separate the model's own
                    susceptibility from properties of the surrounding system, with a quantified defense analysis.
                  </p>
                  <span className="coming-soon">Details after peer review concludes</span>
                </div>
                <div className="featured-card research-card">
                  <div className="card-header">
                    <span className="card-badge ongoing">Ongoing Research</span>
                  </div>
                  <h4>Defending LLM Agents at Execution Time</h4>
                  <p>
                    Screening the actions an autonomous agent proposes before they execute, using signals from
                    the agent's own history rather than trusting attacker-influenceable inputs — with explicit,
                    tunable control over how often benign behavior is flagged.
                  </p>
                  <span className="coming-soon">In preparation</span>
                </div>
              </div>
            </div>

            <div className="category-section">
              <h3 className="category-title">
                <span className="category-icon"></span>
                Masters Research Project
              </h3>
              <div className="featured-grid">
                <div className="featured-card research-card">
                  <div className="card-header">
                    <span className="card-badge course">Research</span>
                  </div>
                  <h4>EdgeML and Cybersecurity: Using Machine Learning Algorithms to detect impostor edge devices </h4>
                  <p>This research focuses on security, specifically impersonation in a sensor network.
                      This research is to build a light impostor detector ML model for edge devices. The
                      mode is also expected to detect sequence transitions if the impostor starts trans-
                      mitting after its data is transmitted.</p>
                  <a href="https://drive.google.com/file/d/1Zh-IXxYftJmgB37RXKo7kX4sslCkgrov/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="github-link">
                    View on Google Drive
                  </a>
                  <span className="tech-stack">Raspberry PI, TensorFlow Lite, Python</span>
                </div>
              </div>
            </div>

            {/* Open-source projects & writing */}
            <div className="category-section">
              <h3 className="category-title">
                <span className="category-icon"></span>
                Open-Source Projects & Writing
              </h3>
              <div className="featured-grid">
                <div className="featured-card research-card full-width-card">
                  <div className="card-header">
                    <span className="card-badge ongoing">Open-Source Project · Part 1 live</span>
                  </div>
                  <h4>Tokens to Agents — Building the LLM Stack from First Principles</h4>
                  <p>
                    A seven-part open-source series and writing project rebuilding the path from classical language models to secure AI
                    agents: n-grams, embeddings, neural language modeling, transformers, LLMs and prompting,
                    agentic AI, and agentic AI security. Each part ships code, a technical article, and an
                    interactive demo.
                  </p>
                  <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                    <a href="https://github.com/NahomMA/tokens-to-agents" target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', fontWeight: 600 }}>
                      GitHub <FaGithub style={{ fontSize: '0.85rem' }} />
                    </a>
                    <a href="https://medium.com/@nahombirhan/the-textbook-fix-that-made-my-language-model-5-worse-e8586d5639b5" target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', fontWeight: 600 }}>
                      Part 1 on Medium
                    </a>
                    <a href="https://huggingface.co/spaces/Nahom-M/tokens-to-agents-01-ngrams" target="_blank" rel="noopener noreferrer" style={{ color: '#3182ce', fontWeight: 600 }}>
                      Interactive Demo
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Projects */}
            <div className="category-section">
              <h3 className="category-title">
                <span className="category-icon"></span>
                Course Projects
              </h3>
              <div className="featured-grid">
                <div className="featured-card course-card">
                  <div className="card-header">
                    <span className="card-badge course">Course</span>
                  </div>
                  <h4>Blockchain-Based Healthcare Data Security</h4>
                  <p>Proposed system integrating blockchain and IPFS for secure medical records management.</p>
                  <a href="https://drive.google.com/file/d/1h43EViKmhozlZESDcwPkf-vIL4SdNVfo/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="github-link">
                    View on Google Drive
                  </a>
                  <span className="tech-stack">IPFS, AWS, Blockchain</span>
                </div>
                <div className="featured-card course-card">
                  <div className="card-header">
                    <span className="card-badge course">Course</span>
                  </div>
                  <h4>Malware Detection</h4>
                  <p>Machine learning-based malware detection system with comprehensive analysis tools.</p>
                  <a href="https://github.com/NahomMA/malware_detection" target="_blank" rel="noopener noreferrer" className="github-link">
                    View on GitHub <FaGithub />
                  </a>

                </div>

              </div>
            </div>

            {/* Industry Projects */}
            <div className="category-section">
              <h3 className="category-title">
                <span className="category-icon"></span>
                Industry Projects
              </h3>
              <div className="featured-grid">
                <div className="featured-card industry-card">
                  <div className="card-header">
                    <span className="card-badge industry">Industry</span>
                    <span className="team-badge">Team</span>
                  </div>
                  <h4>CS Investigations Camera Solutions Platform</h4>
                  <p>Developed comprehensive camera solutions platform for investigative purposes.</p>
                  <span className="tech-stack">Full-stack development, Camera APIs</span>
                </div>

                <div className="featured-card industry-card">
                  <div className="card-header">
                    <span className="card-badge industry">Industry</span>
                  </div>
                  <h4>Dumpster Fullness Detection</h4>
                  <p>Computer vision system for monitoring dumpster capacity and optimizing waste collection.</p>
                  <span className="tech-stack">Computer Vision, IoT, ML</span>
                </div>
                <div className="featured-card industry-card">
                  <div className="card-header">
                    <span className="card-badge industry">Industry</span>
                  </div>
                  <h4>TinyML Tool Assessment and Prototyping</h4>
                  <p>Evaluated and prototyped TinyML tools for edge device machine learning applications.</p>
                  <span className="tech-stack">TinyML, Edge Computing, ML</span>
                </div>
              </div>
            </div>

            {/* Fun Projects */}
            <div className="category-section">
              <h3 className="category-title">
                <span className="category-icon"></span>
                Fun Projects
              </h3>
              <div className="featured-grid">
                <div className="featured-card fun-card">
                  <div className="card-header">
                    <span className="card-badge fun">Fun</span>
                  </div>
                  <h4>Prime and Composite Classification</h4>
                  <p>Created dataset of 2 million numbers, developed models using sequence models and 1D-CNN.</p>
                  <a href="https://github.com/NahomMA/Prime_and_composite_num_lassification" target="_blank" rel="noopener noreferrer" className="github-link">
                    View on GitHub <FaGithub />
                  </a>
                  <span className="tech-stack">Python, TensorFlow, NumPy</span>
                </div>


              </div>
            </div>
          </div>

          <div className="section-footer">
            <Link to="/projects" className="btn btn-outline">
              View All Research & Projects
              <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </section>

        {/* Connect Section */}
        <section className="connect-section">
          <div className="section-header">
            <h2>Let's Connect</h2>
            <p>Interested in AI security, research collaboration, or just want to chat?</p>
          </div>
          <div className="social-links">
            <a href="https://www.linkedin.com/in/nahombirhan/" target="_blank" rel="noopener noreferrer" className="social-link">
              <FaLinkedin />
              <span>LinkedIn</span>
            </a>
            <a href="https://github.com/NahomMA" target="_blank" rel="noopener noreferrer" className="social-link">
              <FaGithub />
              <span>GitHub</span>
            </a>
            <a href="https://x.com/NahomMengistu10" target="_blank" rel="noopener noreferrer" className="social-link">
              <FaTwitter />
              <span>Twitter</span>
            </a>
            <a href="https://medium.com/@nahombirhan" target="_blank" rel="noopener noreferrer" className="social-link">
              <FaMedium />
              <span>Medium</span>
            </a>

              <Link to="/cv" className="btn btn-primary">
                View Full CV
                <FaArrowRight className="btn-icon" />
              </Link>

          </div>
        </section>
      </main>
    </>
  );
};

export default Home;